import { entries, fromEntries } from "remeda";

import { FinalAnswers } from "~/form/flow-machine";
import { merkmaleByYear } from "~/mietspiegel/merkmale";
import { parseAdresse } from "~/utils";
import {
  getSelectedWohnflaecheSpanne,
  getWohnflaecheSpanneOptions,
} from "~/utils/wohnflaecheSpanne";

import { baujahrSpannenByMietspiegeljahr } from "../mietspiegel/baujahrSpannen";
import { Mietspiegeljahr } from "../mietspiegel/types";
import {
  AnswerMerkmalStateMapping,
  answersToMerkmalStateMapping,
} from "./mappings/merkmale";
import { answersToSondermerkmalStateMapping } from "./mappings/sondermerkmale";
import { vertragsdatumToMietspiegelJahrMapping } from "./mappings/vertragsdatum";
import { mapAnswerToMerkmalState } from "./utils/mapAnswerToMerkmalState";
import {
  mapMerkmalStateToMerkmalGruppen,
  MerkmalGruppenStateList,
  MerkmalStateList,
  SondermerkmalStateList,
} from "./utils/mapMerkmalStateToMerkmalGruppen";

export const getTyp = (
  answers: FinalAnswers,
  visibleQuestionAliases: Set<string>,
) => {
  const alias = "Typ";
  return answers && visibleQuestionAliases?.has(alias)
    ? answers[alias]
    : undefined;
};

export const getMietspiegeljahr = (
  answers: FinalAnswers,
): Mietspiegeljahr | undefined => {
  const alias = "Vertragsdatum";

  // If contract is not signed, use newest Mietspiegel
  if (answers["Unterschrieben"] == "Nein") {
    return "2026";
  }

  return answers[alias]
    ? vertragsdatumToMietspiegelJahrMapping[answers[alias]]
    : undefined;
};

export const getAdresse = (
  answers: FinalAnswers,
  visibleQuestionAliases: Set<string>,
): ReturnType<typeof parseAdresse> | undefined => {
  const alias = "Adresse";
  return answers[alias] && visibleQuestionAliases.has(alias)
    ? parseAdresse(answers[alias] as string)
    : undefined;
};

export const getOst = (
  answers: FinalAnswers,
  visibleQuestionAliases: Set<string>,
): boolean => {
  const alias = "Ost";
  return answers[alias] && visibleQuestionAliases.has(alias)
    ? answers[alias]
    : false;
};

export const getWohnlage = (
  answers: FinalAnswers,
  visibleQuestionAliases: Set<string>,
) => {
  const alias = "Wohnlage";
  return answers[alias] && visibleQuestionAliases.has(alias)
    ? answers[alias]
    : undefined;
};

export const getWohnflaeche = (
  answers: FinalAnswers,
  visibleQuestionAliases: Set<string>,
) => {
  const alias = "Qm";
  return answers && visibleQuestionAliases?.has(alias)
    ? Number(answers[alias])
    : undefined;
};

export const getWohnflaecheSpanne = (
  answers: FinalAnswers,
  visibleQuestionAliases: Set<string>,
) => {
  const mietspiegeljahr = getMietspiegeljahr(answers);
  const baujahrSpanne = getBaujahrSpanne(answers, visibleQuestionAliases);
  const wohnlage = getWohnlage(answers, visibleQuestionAliases);
  const wohnflaeche = getWohnflaeche(answers, visibleQuestionAliases);

  if (wohnflaeche === undefined || Number.isNaN(wohnflaeche)) return undefined;
  const wohnflaecheSpanneOptions = getWohnflaecheSpanneOptions(
    mietspiegeljahr,
    baujahrSpanne,
    wohnlage,
  );
  return getSelectedWohnflaecheSpanne(wohnflaeche, wohnflaecheSpanneOptions);
};

export const getVertragsart = (
  answers: FinalAnswers,
  visibleQuestionAliases: Set<string>,
) => {
  const alias = "Mietart";
  return answers && visibleQuestionAliases?.has(alias)
    ? answers[alias]
    : undefined;
};

export const getBaujahr = (
  answers: FinalAnswers,
  visibleQuestionAliases: Set<string>,
) => {
  const alias = "Baujahr";
  return answers && visibleQuestionAliases?.has(alias)
    ? Number(answers[alias])
    : undefined;
};

export const getBaujahrSpanne = (
  answers: FinalAnswers,
  visibleQuestionAliases: Set<string>,
) => {
  const mietspiegeljahr = getMietspiegeljahr(answers);
  const baujahr = getBaujahr(answers, visibleQuestionAliases);
  const ost = getOst(answers, visibleQuestionAliases);

  if (baujahr && mietspiegeljahr) {
    const baujahrSpannen =
      baujahrSpannenByMietspiegeljahr[mietspiegeljahr as Mietspiegeljahr];

    return baujahrSpannen.find((baujahrSpanne) => {
      const [start, end] = baujahrSpanne
        .replace(/^(W:|O:)/, "")
        .split("-")
        .map(Number);
      const isOst = baujahrSpanne.includes("O:");
      const isWest = baujahrSpanne.includes("W:");

      const matchesOstWest =
        (!isOst && !isWest) || // No specific region
        (isOst && ost === true) || // Matches East
        (isWest && ost === false); // Matches West

      return baujahr >= start && baujahr <= end && matchesOstWest;
    });
  }

  return undefined;
};

export const getNettokaltmiete = (
  answers: FinalAnswers,
  visibleQuestionAliases: Set<string>,
): number | undefined => {
  const alias = "Nettokaltmiete";
  return answers[alias] && visibleQuestionAliases?.has(alias)
    ? Number(answers[alias])
    : undefined;
};

export const getGeforderteNettokaltmiete = (
  answers: FinalAnswers,
  visibleQuestionAliases: Set<string>,
): number | undefined => {
  const alias = "Geforderte Nettokaltmiete";
  return answers[alias] && visibleQuestionAliases?.has(alias)
    ? Number(answers[alias])
    : undefined;
};

export const getAusstattung = (
  answers: FinalAnswers,
  visibleQuestionAliases: Set<string>,
) => {
  const alias_sh = "Wohnung hat Sammelheizung";
  const alias_bad = "Badezimmer in Wohnung";

  return {
    sammelheizung:
      answers[alias_sh] && visibleQuestionAliases?.has(alias_sh)
        ? answers[alias_sh]
        : "Nein",
    bad:
      answers[alias_bad] && visibleQuestionAliases?.has(alias_bad)
        ? answers[alias_bad]
        : "Nein",
  };
};

export const getMerkmalStatesByGruppe = (
  answers: FinalAnswers,
  visibleQuestionAliases: Set<string>,
): MerkmalGruppenStateList => {
  const merkmale = getMerkmalStates(answers, visibleQuestionAliases);
  return mapMerkmalStateToMerkmalGruppen(merkmale);
};

export const getMerkmalStates = (
  answers: FinalAnswers,
  visibleQuestionAliases: Set<string>,
): MerkmalStateList => {
  const mietspiegelJahr = getMietspiegeljahr(answers);

  if (mietspiegelJahr) {
    const merkmale = merkmaleByYear[mietspiegelJahr];

    return fromEntries(
      entries(answersToMerkmalStateMapping)
        .filter(([merkmal]) => merkmale.has(merkmal))
        .map(
          ([key, answerMerkmalMapping]: [
            string,
            AnswerMerkmalStateMapping,
          ]) => {
            return [
              key,
              mapAnswerToMerkmalState(
                answerMerkmalMapping,
                answers,
                visibleQuestionAliases,
              ),
            ];
          },
        ),
    );
  }
  return {};
};

export const getSondermerkmalStates = (
  answers: FinalAnswers,
  visibleQuestionAliases: Set<string>,
): SondermerkmalStateList | undefined => {
  const mietspiegeljahr = getMietspiegeljahr(answers);

  if (mietspiegeljahr == "2015") {
    return fromEntries(
      entries(answersToSondermerkmalStateMapping).map(
        ([key, answerMerkmalMapping]: [string, any]) => {
          return [
            key,
            mapAnswerToMerkmalState(
              answerMerkmalMapping,
              answers,
              visibleQuestionAliases,
            ),
          ];
        },
      ),
    ) as SondermerkmalStateList;
  }

  return undefined;
};
